import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getInvoicePdf } from '../../../services/InvoicesService';
import InvoicePdfViewer from './InvoicePdfViewer';

// The invoice on screen IS the customer's PDF: the server renders it (backend/services/invoice_pdf.py, GET /invoices/<id>/pdf) and this page
// only shows that file in a viewer, so the on-screen invoice can never differ from the download, the print or the email attachment.
// The PDF is fetched with the admin login (blob URL, never a public link); Download and Print use the SAME fetched file.
// An order without an issued invoice (a concept) has no PDF: the buttons are disabled and a message says so.

function pickDoc(order, invoiceId) {
    const docs = (order && order.invoices) || [];
    if (invoiceId) return docs.find((d) => d.id === invoiceId) || null;
    return docs.find((d) => (d.kind === 'invoice' || d.kind === 'marketing') && !d.is_legacy)
        || docs.find((d) => d.kind === 'invoice' || d.kind === 'marketing') || null;      // only an old (legacy) invoice: shown as "no PDF available"
}

const OrderInvoice = forwardRef(function OrderInvoice({ order, initialLang, invoiceId }, ref) {
    const { t } = useTranslation();
    const [invoiceLang, setInvoiceLang] = useState(initialLang || 'nl');
    const doc = pickDoc(order, invoiceId);
    const docId = doc && !doc.is_legacy ? doc.id : null;
    const isLegacy = !!(doc && doc.is_legacy);
    const [pdf, setPdf] = useState({ status: 'idle', blob: null });
    const urlsRef = useRef([]);

    // fetch the PDF through the authenticated API (again when the language changes)
    useEffect(() => {
        if (!docId) { setPdf({ status: 'idle', blob: null }); return undefined; }
        let cancelled = false;
        setPdf({ status: 'loading', blob: null });
        getInvoicePdf(docId, invoiceLang)
            .then((res) => { if (!cancelled) setPdf({ status: 'ready', blob: res.data }); })
            .catch(() => { if (!cancelled) setPdf({ status: 'error', blob: null }); });
        return () => { cancelled = true; };
    }, [docId, invoiceLang]);

    // every blob URL made here is revoked when the page is left
    useEffect(() => () => { urlsRef.current.forEach((u) => window.URL.revokeObjectURL(u)); urlsRef.current = []; }, []);

    const blobUrl = () => {
        const url = window.URL.createObjectURL(pdf.blob);
        urlsRef.current.push(url);
        return url;
    };

    const handleDownload = () => {
        if (!pdf.blob) return;
        const link = document.createElement('a');
        link.href = blobUrl();
        link.download = `${invoiceLang}-invoice-${(doc && doc.invoice_number) || (order && order.order_number) || 'order'}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    // Print = the same PDF in a new tab (print it from the viewer). The tab is opened on the click (popup blockers), then pointed at the file.
    const handlePrint = () => {
        if (!pdf.blob) return;
        const win = window.open('', '_blank');
        if (!win) { toast.error(t('pdf.popupBlocked')); return; }
        win.location.href = blobUrl();
    };

    if (!order) return null;
    const ready = pdf.status === 'ready';
    const label = (doc && doc.invoice_number) ? `${t('pdf.invoiceLabel')} ${doc.invoice_number}` : t('pdf.invoiceLabel');

    return (
        <>
            <div className="mb-3 no-print order-invoice-actions d-flex align-items-center gap-2 flex-wrap">
                <Button variant="outline-primary" size="sm" onClick={handlePrint} disabled={!ready} data-testid="invoice-print-pdf">
                    <i className="fa-solid fa-print me-1" /> {t('pdf.printInvoice')}
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={handleDownload} disabled={!ready} data-testid="invoice-download-pdf">
                    <i className="fa-solid fa-download me-1" /> {t('pdf.downloadPdf')}
                </Button>
                <div className="d-flex gap-1 ms-2" style={{ borderLeft: '1px solid #dee2e6', paddingLeft: 10 }}>
                    <Button size="sm" variant={invoiceLang === 'nl' ? 'primary' : 'outline-secondary'} onClick={() => setInvoiceLang('nl')} style={{ minWidth: 46, fontSize: 12 }}>
                        🇳🇱 NL
                    </Button>
                    <Button size="sm" variant={invoiceLang === 'en' ? 'primary' : 'outline-secondary'} onClick={() => setInvoiceLang('en')} style={{ minWidth: 46, fontSize: 12 }}>
                        🇬🇧 EN
                    </Button>
                </div>
            </div>

            {!docId && (
                <Alert variant="secondary" data-testid="invoice-no-pdf">
                    {isLegacy ? t('pdf.legacyNoPdf') : t('pdf.noInvoiceYet')}
                </Alert>
            )}
            {docId && pdf.status === 'loading' && <div className="text-center py-4" data-testid="invoice-pdf-loading"><Spinner animation="border" size="sm" /> {t('pdf.loading')}</div>}
            {docId && pdf.status === 'error' && <Alert variant="danger" data-testid="invoice-pdf-failed">{t('pdf.loadFailed')}</Alert>}
            {ready && <InvoicePdfViewer blob={pdf.blob} label={label} />}
        </>
    );
});

export default OrderInvoice;
